import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import Container from "../../componentes/Container";
import BarraDeNavegacao from "../../componentes/BarraDeNavegacao";
import { CampoDeTexto } from "../../componentes";

import { buscarPermissoesUsuarios } from "./services/buscarPermissoesUsuarios";
import { adicionarPermissao } from "./services/adicionarPermissao";
import { removerPermissao } from "./services/removerPermissao";
import { queryClient } from "../../utils/query";
import { useParams } from "react-router-dom";
import "./styles.scss";
import Paginacao from "./components/Paginacao";

interface UsuarioPermissao {
  id: number;
  nome: string;
  login: string;
  permissaoAtiva: boolean;
}

const GerenciarPermissoesLocaisDeAtendimento = () => {
  const [filtroNome, setFiltroNome] = useState("");
  const [filtroLogin, setFiltroLogin] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [resetarPaginacao, setResetarPaginacao] = useState(Date.now());

  const [usuariosComPermissao, setUsuariosComPermissao] = useState<
    UsuarioPermissao[]
  >([]);

  const { id } = useParams<{ id: string }>();
  const idLocalDeAtendimento = id;

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "usuariosPermissoes",
      idLocalDeAtendimento,
      filtroNome,
      filtroLogin,
      paginaAtual,
    ],
    queryFn: () =>
      buscarPermissoesUsuarios(
        idLocalDeAtendimento,
        filtroNome,
        filtroLogin,
        paginaAtual
      ),
  });

  const { mutate: alterarPermissao, isPending: alterandoPermissao } =
    useMutation({
      mutationFn: async ({
        idUsuario,
        permissaoAtiva,
      }: {
        idUsuario: number;
        permissaoAtiva: boolean;
      }) => {
        if (!idLocalDeAtendimento)
          throw new Error("idLocalDeAtendimento inválido");
        return permissaoAtiva
          ? removerPermissao(idLocalDeAtendimento, idUsuario)
          : adicionarPermissao(idLocalDeAtendimento, idUsuario);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            "usuariosPermissoes",
            idLocalDeAtendimento,
            filtroNome,
            filtroLogin,
            paginaAtual,
          ],
        });
      },
      onError: (e) => {
        console.error("Erro ao alterar permissão", e);
        alert("Erro ao alterar permissão");
      },
    });

  useEffect(() => {
    if (data?.modelo) {
      const listaUsuarios = data.modelo.map((item: any) => ({
        id: item.id,
        nome: item.nome,
        login: item.login,
        permissaoAtiva: item.possuiPermissaoNoLocal,
      }));
      setUsuariosComPermissao(listaUsuarios);
    }
  }, [data]);

  function irParaPagina(pagina: number) {
    if (pagina <= 0) return;
    if (data && pagina > data.totalDePaginas) return;
    setPaginaAtual(pagina);
    setResetarPaginacao(Date.now());
  }

  const colunas = ["Usuário", "Login", "Permissão"];

  return (
    <>
      <BarraDeNavegacao
        titulo="Gerencie as permissões de AGEHAB"
        comBotaoADireita={false}
      />

      <Container comFundo tamanhoDoContainer="grande" comCabecalhoFixo>
        <div className="formulario">
          <h2 className="cartao__titulo">Gerencie as permissões de AGEHAB</h2>
          <p className="u-texto-pequeno u-margem-top-pequena">
            Busque ou filtre usuários
          </p>

          <div className="grade__linha u-margem-superior-media">
            <CampoDeTexto
              name="filtroNome"
              label="Buscar por nome do usuário"
              value={filtroNome}
              onChange={(e) => {
                setFiltroNome(e.target.value);
                irParaPagina(1);
              }}
              onBlur={() => {}}
              placeholder="Busque um nome"
              colunas="4"
            />
            <CampoDeTexto
              name="filtroLogin"
              label="Buscar por login"
              value={filtroLogin}
              onChange={(e) => {
                setFiltroLogin(e.target.value);
                irParaPagina(1);
              }}
              onBlur={() => {}}
              placeholder="Busque um login"
              colunas="4"
            />
          </div>

          <table className="tabela tabela_com-largura-total tabela_listrada tabela_com-borda u-margem-superior-media">
            <thead>
              <tr>
                {colunas.map((coluna) => (
                  <th key={coluna} className="u-texto-alinhado-ao-centro">
                    {coluna}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    {colunas.map((_, j) => (
                      <td key={j}>
                        <div className="skeleton skeleton-linha" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td
                    colSpan={colunas.length}
                    className="u-texto-alinhado-ao-centro u-cor-vermelha"
                  >
                    Ocorreu um erro ao carregar os dados.
                  </td>
                </tr>
              ) : usuariosComPermissao.length === 0 ? (
                <tr>
                  <td
                    colSpan={colunas.length}
                    className="u-texto-alinhado-ao-centro"
                  >
                    Nenhum usuário encontrado.
                  </td>
                </tr>
              ) : (
                usuariosComPermissao.map((usuario) => (
                  <tr key={usuario.id}>
                    <td>{usuario.nome}</td>
                    <td className="u-texto-alinhado-ao-centro">
                      {usuario.login}
                    </td>
                    <td className="u-texto-alinhado-ao-centro">
                      <label className="formulario__switch">
                        <input
                          type="checkbox"
                          checked={usuario.permissaoAtiva}
                          disabled={alterandoPermissao}
                          onChange={() => {
                            alterarPermissao({
                              idUsuario: usuario.id,
                              permissaoAtiva: usuario.permissaoAtiva,
                            });
                          }}
                        />
                        <span className="formulario__slider" />
                      </label>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {data && (
            <>
              <div className="u-margem-top-media u-texto-pequeno">
                {data.totalDeRegistros} resultado(s)
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "1rem",
                }}
              >
                <Paginacao
                  paginaAtual={paginaAtual}
                  totalDePaginas={data.totalDePaginas}
                  onPageChange={irParaPagina}
                />
              </div>
            </>
          )}
        </div>
      </Container>
    </>
  );
};

export default GerenciarPermissoesLocaisDeAtendimento;
